import { z } from 'zod';

export const itineraryBuilderSlotKind = [
  'morning',
  'lunch',
  'afternoon',
  'dinner',
  'hotel',
  'transfer',
] as const;
export const itineraryBuilderSlotKindSchema = z.enum(itineraryBuilderSlotKind);
export type ItineraryBuilderSlotKind = z.infer<
  typeof itineraryBuilderSlotKindSchema
>;

export const itineraryBuilderRoleSchema = z.enum(['primary', 'optional']);
export type ItineraryBuilderRole = z.infer<typeof itineraryBuilderRoleSchema>;

export const itineraryBuilderScorePartsSchema = z.object({
  popularity: z.number(),
  keyword: z.number(),
  city: z.number(),
  coOccurrence: z.number(),
});
export type ItineraryBuilderScoreParts = z.infer<
  typeof itineraryBuilderScorePartsSchema
>;

export const itineraryBuilderEvidenceSchema = z.object({
  coTrips: z.number().int().nonnegative(),
  distinctAccounts: z.number().int().nonnegative().nullable(),
  lift: z.number().nonnegative().nullable(),
  soldTrips: z.number().int().nonnegative(),
  cancelledTrips: z.number().int().nonnegative().nullable(),
  /** Optional decomposition of the ranker score ("why this item"). */
  scoreParts: itineraryBuilderScorePartsSchema.optional(),
  /** Same-day sold trips with the stay hotel (when known). */
  hotelPairSameDay: z.number().int().nonnegative().optional(),
  /** Same-trip sold co-occurrence with the stay hotel. */
  hotelPairTrips: z.number().int().nonnegative().optional(),
});

export const itineraryBuilderQualityMetricsSchema = z.object({
  daysWithHotelPct: z.number().min(0).max(1),
  checkInWithTransferPct: z.number().min(0).max(1),
  activitiesWithSoldEvidencePct: z.number().min(0).max(1),
  avgDistanceFromHotelKm: z.number().nullable(),
  hardRouteViolations: z.number().int().nonnegative(),
  softRouteWarnings: z.number().int().nonnegative(),
  interestCoverage: z.number().min(0).max(1),
  activityDiversity: z.number().min(0).max(1),
  readyForCommit: z.boolean(),
});
export type ItineraryBuilderQualityMetrics = z.infer<
  typeof itineraryBuilderQualityMetricsSchema
>;
export type ItineraryBuilderEvidence = z.infer<
  typeof itineraryBuilderEvidenceSchema
>;

export const itineraryBuilderSlotSchema = z.object({
  slot: itineraryBuilderSlotKindSchema,
  productId: z.uuid(),
  title: z.string(),
  city: z.string().nullable(),
  contentCategory: z.string().nullable(),
  role: itineraryBuilderRoleSchema,
  evidence: itineraryBuilderEvidenceSchema,
  /**
   * Catalog presentation for collab-style cards (CUS/CON item cards).
   * Filled by preview hydrate; optional so pure-assemble paths stay light.
   */
  country: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
  thumbnail: z.string().nullable().optional(),
  /** Raw product.attachments JSON — client resolves gallery URLs. */
  attachments: z.json().optional(),
  /** Catalog hashtags from `products.product.hashtags` (for card chips). */
  hashtags: z.array(z.string().min(1).max(80)).max(40).optional(),
  /** WGS84 lat for map pins (from product.location_point). */
  lat: z.number().min(-90).max(90).nullable().optional(),
  /** WGS84 lng for map pins. */
  lng: z.number().min(-180).max(180).nullable().optional(),
  serviceType: z.string().nullable().optional(),
  transportType: z.string().nullable().optional(),
  /** Resolved activity duration (product.legs or default slot minutes). */
  durationMinutes: z.number().int().nonnegative().nullable().optional(),
  /**
   * Timeline start minutes from midnight (day graph).
   * May exceed 24h when earlier legs push the cursor past night (packing cascade).
   */
  startMinutes: z
    .number()
    .int()
    .min(0)
    .max(48 * 60)
    .optional(),
  /** Timeline end minutes from midnight (day graph). */
  endMinutes: z
    .number()
    .int()
    .min(0)
    .max(48 * 60 + 12 * 60)
    .optional(),
  /** True when activity metro ≠ overnight hotel (needs transfer). */
  isDayTrip: z.boolean().optional(),
  /**
   * True when this transfer is an intercity hop
   * (checkout city A → travel → check-in city B).
   */
  isIntercity: z.boolean().optional(),
  /**
   * Semantic role of a transfer stop — source of truth for day-trip
   * sandwiches (not kind-sort). Arrival/relocation are stay-bound;
   * outbound/return bookend a day-trip activity.
   */
  transferDirection: z
    .enum(['arrival', 'departure', 'relocation', 'outbound', 'return'])
    .optional(),
  /**
   * Stable relative order within a day (preserves day-trip sandwich
   * transfer→activity→transfer through kind-based sorts).
   */
  timelineOrder: z.number().int().optional(),
  /**
   * Why this product is on the draft:
   * - `sold` — ranked from sold-history (default when omitted)
   * - `catalog` — org catalog fill after sold pool ran out (location/hashtag/stars)
   */
  recommendationSource: z.enum(['sold', 'catalog']).optional(),
});
export type ItineraryBuilderSlot = z.infer<typeof itineraryBuilderSlotSchema>;

/**
 * Ordered route legs for a day (semantic day graph, not slot-kind soup).
 * Stops are activities/hotel/dining; transfers carry direction.
 */
export const itineraryBuilderRouteLegSchema = z.object({
  kind: z.enum(['stop', 'transfer']),
  productId: z.uuid(),
  title: z.string(),
  slot: itineraryBuilderSlotKindSchema,
  startMinutes: z.number().int().nonnegative().optional(),
  endMinutes: z.number().int().nonnegative().optional(),
  durationMinutes: z.number().int().nonnegative().optional(),
  isDayTrip: z.boolean().optional(),
  transferDirection: z
    .enum(['arrival', 'departure', 'relocation', 'outbound', 'return'])
    .optional(),
  /** Edge from previous leg (null for first). */
  fromPrevDistanceKm: z.number().nullable().optional(),
  fromPrevTravelMinutes: z.number().nullable().optional(),
});
export type ItineraryBuilderRouteLeg = z.infer<
  typeof itineraryBuilderRouteLegSchema
>;

export const itineraryBuilderDaySchema = z.object({
  offset: z.number().int().min(0),
  metroCluster: z.string().nullable(),
  city: z.string().nullable(),
  hotelStay: z
    .object({
      productId: z.uuid(),
      title: z.string(),
      city: z.string().nullable(),
      nights: z.number().int().positive(),
    })
    .nullable(),
  slots: z.array(itineraryBuilderSlotSchema),
  /** Ordered route model for the day (optional on wire for older clients). */
  routeLegs: z.array(itineraryBuilderRouteLegSchema).optional(),
  coherenceViolations: z.array(z.string()).default([]),
});
export type ItineraryBuilderDay = z.infer<typeof itineraryBuilderDaySchema>;

export const itineraryBuilderPreviewInput = z.object({
  country: z.string().min(1).max(120),
  cities: z.array(z.string().min(1).max(120)).max(20).default([]),
  month: z.number().int().min(1).max(12).optional(),
  pax: z.number().int().min(1).max(40).default(2),
  kids: z.boolean().default(false),
  keywords: z.array(z.string().min(1).max(80)).max(20).default([]),
  durationDays: z.number().int().min(1).max(30).default(7),
  /**
   * Local arrival hour 0–23 for day 0 (airport/train). When ≥16, midday
   * programme is skipped (transfer → hotel → dinner only). When omitted,
   * assume midday arrival (full check-in programme after luggage drop).
   */
  arrivalHour: z.number().int().min(0).max(23).optional(),
  /**
   * Hotel preferences read from the request's notes (XT-806,
   * `hotelPreferencesFromNotes` keys): the stay hotel meeting most of them is
   * picked first. Unknown keys are ignored.
   */
  hotelPreferences: z.array(z.string().min(1).max(40)).max(12).optional(),
  /** The request's pace: relaxed drops Active-paced activities (XT-806). */
  pace: z.enum(['relaxed', 'balanced', 'active']).optional(),
  /** Mini Planner Generate (XT-890): leave out products hidden there. */
  forMiniPlanner: z.boolean().optional(),
});
export type ItineraryBuilderPreviewInput = z.infer<
  typeof itineraryBuilderPreviewInput
>;

export const itineraryBuilderJourneyStopSchema = z.object({
  dayOffset: z.number().int().nonnegative(),
  city: z.string().nullable(),
  metroCluster: z.string().nullable(),
  kind: z.enum(['stay', 'transfer', 'day-trip', 'empty']),
  hotelTitle: z.string().nullable(),
  hotelNights: z.number().int().positive().nullable(),
  hardViolations: z.number().int().nonnegative(),
});
export type ItineraryBuilderJourneyStop = z.infer<
  typeof itineraryBuilderJourneyStopSchema
>;

/** Ranked runner-up catalog for harness “why this / why not”. */
export const itineraryBuilderCandidateSchema = z.object({
  productId: z.uuid(),
  title: z.string(),
  city: z.string().nullable(),
  contentCategory: z.string().nullable(),
  soldTrips: z.number().int().nonnegative(),
  cancelledTrips: z.number().int().nonnegative(),
  score: z.number(),
  keywordHits: z.number().int().nonnegative().optional(),
  coTrips: z.number().int().nonnegative().optional(),
  lift: z.number().nullable().optional(),
  distinctAccounts: z.number().int().nonnegative().nullable().optional(),
  /** Whether this product was placed in the draft day grid. */
  placement: z.enum(['placed', 'ranked']),
});
export type ItineraryBuilderCandidate = z.infer<
  typeof itineraryBuilderCandidateSchema
>;

/**
 * LLM advisory review of a builder draft (opinion + soft error scan).
 * Does not block commit — deterministic hard validation still owns that.
 */
export const itineraryBuilderAiReviewIssueSchema = z.object({
  severity: z.enum(['error', 'warning', 'note']),
  /** 1-based day number when the issue is day-specific. */
  day: z.number().int().positive().nullable(),
  message: z.string().min(1).max(500),
});
export type ItineraryBuilderAiReviewIssue = z.infer<
  typeof itineraryBuilderAiReviewIssueSchema
>;

export const itineraryBuilderAiDayTeaserSchema = z.object({
  /** 1-based day number. */
  day: z.number().int().positive(),
  /** One-line guest/consultant blurb for the day. */
  teaser: z.string().min(1).max(200),
});
export type ItineraryBuilderAiDayTeaser = z.infer<
  typeof itineraryBuilderAiDayTeaserSchema
>;

export const itineraryBuilderAiReviewSchema = z.object({
  /** Short consultant-facing verdict (1–3 sentences). */
  opinion: z.string().min(1).max(1200),
  /** What looks good / overall fit for the brief. */
  summary: z.string().min(1).max(1200),
  issues: z.array(itineraryBuilderAiReviewIssueSchema).max(20),
  /** Optional short teaser per day (AI narrative layer). */
  dayTeasers: z.array(itineraryBuilderAiDayTeaserSchema).max(30).optional(),
  /** How the model rates the draft overall. */
  rating: z.enum(['strong', 'ok', 'weak', 'broken']),
  /** ms spent in the AI call (0 when skipped). */
  latencyMs: z.number().nonnegative(),
  /** Present when review was skipped or failed (still advisory). */
  skippedReason: z.string().max(200).nullable().optional(),
});
export type ItineraryBuilderAiReview = z.infer<
  typeof itineraryBuilderAiReviewSchema
>;

/**
 * Async AI review for a draft already returned by `preview`.
 * Client sends brief + days so we do not re-run sold ranking.
 */
export const itineraryBuilderReviewDraftInput = z.object({
  brief: itineraryBuilderPreviewInput,
  days: z.array(itineraryBuilderDaySchema).min(1).max(30),
  journeySummary: z.string().max(500).optional(),
  readyForCommit: z.boolean().optional(),
});
export type ItineraryBuilderReviewDraftInput = z.infer<
  typeof itineraryBuilderReviewDraftInput
>;

export const itineraryBuilderPreviewOutput = z.object({
  days: z.array(itineraryBuilderDaySchema),
  meta: z.object({
    corpusVersion: z.string().nullable(),
    organizationId: z.string().uuid(),
    /**
     * Ranking weights actually used (sold popularity dominates).
     * `history` kept for wire compat — always 0; org sold corpus *is* the history.
     */
    weights: z.object({
      popularity: z.number(),
      coOccurrence: z.number(),
      keyword: z.number(),
      city: z.number(),
      history: z.number(),
    }),
    latencyMs: z.number().nonnegative(),
    candidateCount: z.number().int().nonnegative(),
    coOccurEdges: z.number().int().nonnegative().default(0),
    degraded: z.string().nullable(),
    /** Meeting: rank from sold only; cancels demote by excess rate. */
    corpusPolicy: z
      .string()
      .default('sold-only-cancel-rate-demotion')
      .optional(),
    soldStageBand: z.string().default('[10,20)').optional(),
    hardCoherencePassRate: z.number().min(0).max(1).optional(),
    journeySummary: z.string().optional(),
    journeyArc: z.array(itineraryBuilderJourneyStopSchema).optional(),
    /** Short steps for harness / Reasoned rail. */
    reasoningSteps: z.array(z.string()).optional(),
    /** Top ranked products (incl. not placed) for eval catalog. */
    candidates: z.array(itineraryBuilderCandidateSchema).max(40).optional(),
    /** Phase timings (ms) for eval harness performance chip. */
    timings: z
      .object({
        soldCorpusMs: z.number().nonnegative(),
        coOccurMs: z.number().nonnegative(),
        assembleMs: z.number().nonnegative(),
        pairsMs: z.number().nonnegative().optional(),
        validateMs: z.number().nonnegative().optional(),
        aiReviewMs: z.number().nonnegative().optional(),
      })
      .optional(),
    /** Quality metrics (hotel/transfer/route/interest). */
    quality: itineraryBuilderQualityMetricsSchema.optional(),
    /** Commit is blocked while hard validation fails. */
    readyForCommit: z.boolean().optional(),
    /**
     * Optional LLM layer on top of sold ranking + route validation.
     * Advisory only — never flips readyForCommit. Skipped when AI is off.
     */
    aiReview: itineraryBuilderAiReviewSchema.optional(),
  }),
});
export type ItineraryBuilderPreviewOutput = z.infer<
  typeof itineraryBuilderPreviewOutput
>;

/** Opaque draft from preview — client re-sends days for commit. */
export const itineraryBuilderCommitInput = z.object({
  itineraryId: z.uuid(),
  /**
   * Primary + optional slots. Optionals are written with `is_optional=true`
   * (PRD D9 write-back).
   */
  days: z.array(itineraryBuilderDaySchema).min(1).max(30),
});
export type ItineraryBuilderCommitInput = z.infer<
  typeof itineraryBuilderCommitInput
>;

export const itineraryBuilderCommitOutput = z.object({
  itineraryId: z.uuid(),
  versionId: z.uuid(),
  createdItemIds: z.array(z.uuid()),
  /** Item ids created from slots with `role: 'optional'`. */
  optionalItemIds: z.array(z.uuid()).default([]),
  /**
   * Always 0 after optionals write-back (kept for wire/eval compatibility).
   * @deprecated Prefer `optionalItemIds.length`.
   */
  skippedOptionalSlots: z.number().int().nonnegative(),
});
export type ItineraryBuilderCommitOutput = z.infer<
  typeof itineraryBuilderCommitOutput
>;

/**
 * Freeform customer email / request → builder form (same OpenRouter path as
 * agent-panel `parseCustomerEmail` / inbox brief extraction).
 */
export const itineraryBuilderParseBriefInput = z.object({
  /** Pasted customer email or freeform trip request. */
  content: z.string().min(1).max(50_000),
});
export type ItineraryBuilderParseBriefInput = z.infer<
  typeof itineraryBuilderParseBriefInput
>;

export const itineraryBuilderParseBriefOutput = z.object({
  /**
   * Partial builder params to merge into the form (only fields the email
   * supported). Client applies these without auto-running Generate.
   */
  params: itineraryBuilderPreviewInput.partial(),
  /** Which preview-input keys were filled from the email. */
  appliedFields: z.array(
    z.enum([
      'country',
      'cities',
      'month',
      'pax',
      'kids',
      'keywords',
      'durationDays',
      'arrivalHour',
    ])
  ),
  /** Raw LLM extraction (for assumptions / clarifying UI). */
  extracted: z.object({
    travellersTotal: z.number().int().nullable(),
    destinations: z.array(
      z.object({
        city: z.string().nullable(),
        country: z.string().nullable(),
        nights: z.number().int().nullable(),
      })
    ),
    startDate: z.string().nullable(),
    endDate: z.string().nullable(),
    interests: z.array(z.string()),
    budgetLevel: z.enum(['economy', 'medium', 'luxury']).nullable(),
    hasChildren: z.boolean(),
    assumptions: z.array(z.string()),
  }),
  clarifyingQuestions: z.array(
    z.object({
      field: z.string(),
      question: z.string(),
      type: z.enum(['text', 'choice']),
      options: z.array(z.string()).optional(),
    })
  ),
});
export type ItineraryBuilderParseBriefOutput = z.infer<
  typeof itineraryBuilderParseBriefOutput
>;

/** Day-suggest catalogue enrichment: sold/cancel counts for product ids. */
export const itineraryBuilderProductEvidenceInput = z.object({
  country: z.string().min(1).max(120),
  productIds: z.array(z.uuid()).min(1).max(80),
});
export type ItineraryBuilderProductEvidenceInput = z.infer<
  typeof itineraryBuilderProductEvidenceInput
>;

export const itineraryBuilderProductEvidenceRowSchema = z.object({
  productId: z.uuid(),
  soldTrips: z.number().int().nonnegative(),
  cancelledTrips: z.number().int().nonnegative(),
});

export const itineraryBuilderProductEvidenceOutput = z.object({
  rows: z.array(itineraryBuilderProductEvidenceRowSchema),
});
export type ItineraryBuilderProductEvidenceOutput = z.infer<
  typeof itineraryBuilderProductEvidenceOutput
>;

/**
 * XT-951 — the contract prices of a Mini Planner page's dynamic products, for
 * the brief's dates and party: what an itinerary item of each would be priced.
 */
export const itineraryBuilderDynamicPricesInput = z.object({
  productIds: z.array(z.uuid()).min(1).max(80),
  /** The trip's first day. */
  startDate: z.iso.date(),
  /** The stay a hotel is priced for. */
  nights: z.number().int().min(1).max(60),
  travellers: z
    .array(
      z.object({
        travellerTypeId: z.uuid(),
        count: z.number().int().min(0).max(50),
      })
    )
    .max(10),
});
export type ItineraryBuilderDynamicPricesInput = z.infer<
  typeof itineraryBuilderDynamicPricesInput
>;

export const itineraryBuilderDynamicPriceRowSchema = z.object({
  productId: z.uuid(),
  /** The pricing function's output, in the static price shapes. */
  price: z.unknown(),
  pricingType: z.string(),
  currency: z.string(),
  isGross: z.boolean(),
});

export const itineraryBuilderDynamicPricesOutput = z.object({
  rows: z.array(itineraryBuilderDynamicPriceRowSchema),
});
export type ItineraryBuilderDynamicPricesOutput = z.infer<
  typeof itineraryBuilderDynamicPricesOutput
>;

/** Ranked day-suggest catalogue (list + evidence + rankDayCatalog). */
export const itineraryBuilderSuggestCatalogMode = z.enum(['picks', 'pool']);
export type ItineraryBuilderSuggestCatalogMode = z.infer<
  typeof itineraryBuilderSuggestCatalogMode
>;

export const itineraryBuilderSuggestCatalogInput = z.object({
  country: z.string().min(1).max(120),
  /** Extra orgs (CMS network). Empty → caller's org only. */
  organizationIds: z.array(z.uuid()).max(20).optional(),
  city: z.string().min(1).max(120).optional(),
  customRegionIds: z.array(z.uuid()).max(20).optional(),
  briefMoods: z.array(z.string().min(1).max(80)).max(20).optional(),
  excludeProductIds: z.array(z.uuid()).max(200).optional(),
  kindsOnDay: z.array(z.string().min(1).max(40)).max(10).optional(),
  dayCity: z.string().min(1).max(120).optional(),
  hotelPoint: z
    .object({ lat: z.number().finite(), lng: z.number().finite() })
    .optional(),
  /** From the brief: order-only signals (a relaxed party avoids long days). */
  pace: z.enum(['relaxed', 'balanced', 'active']).optional(),
  /** The party includes children: family-suited products lead. */
  childrenInParty: z.boolean().optional(),
  /** picks = final day cards (default); pool = scored shortlist for MCP. */
  mode: itineraryBuilderSuggestCatalogMode.optional(),
  limit: z.number().int().min(1).max(40).optional(),
  poolLimit: z.number().int().min(1).max(120).optional(),
});
export type ItineraryBuilderSuggestCatalogInput = z.infer<
  typeof itineraryBuilderSuggestCatalogInput
>;

export const itineraryBuilderSuggestCatalogProductSchema = z.object({
  id: z.uuid(),
  title: z.string(),
  kind: z.enum(['experience', 'restaurant']),
  city: z.string().nullable(),
  country: z.string().nullable(),
  subcategory: z.string().nullable(),
  tags: z.array(z.string()),
  soldTrips: z.number().int().nonnegative(),
  cancelledTrips: z.number().int().nonnegative(),
  location: z.object({ lat: z.number(), lng: z.number() }).nullable(),
  thumbnail: z.string().nullable(),
  contentCategory: z.string().nullable(),
});

export const itineraryBuilderSuggestCatalogOutput = z.object({
  mode: itineraryBuilderSuggestCatalogMode,
  products: z.array(itineraryBuilderSuggestCatalogProductSchema),
});
export type ItineraryBuilderSuggestCatalogOutput = z.infer<
  typeof itineraryBuilderSuggestCatalogOutput
>;
